import React, { useState } from 'react';
import { X, Phone, Mail, MapPin, Calendar, Briefcase, GraduationCap, Heart, MessageCircle, Edit, Share2, Link2 } from 'lucide-react';
import { useFamilyStore } from '../../store/familyStore';
import { buildUrl } from '../../utils/routing';
import { UnifiedMemberModal } from '../Forms/UnifiedMemberModal';

export const MemberDetailSidebar: React.FC = () => {
  const { selectedMember, setSelectedMember, isAdminView, currentFamilyTreeId } = useFamilyStore();
  const [showEditModal, setShowEditModal] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  if (!selectedMember) return null;

  const calculateAge = (birthDate: string, deathDate?: string) => {
    if (!birthDate) return 'Unknown';
    const birth = new Date(birthDate);
    if (isNaN(birth.getTime())) return 'Unknown';
    const end = deathDate ? new Date(deathDate) : new Date();
    return end.getFullYear() - birth.getFullYear();
  };

  const handleShare = async () => {
    if (!currentFamilyTreeId) return;
    const url = `${window.location.origin}${buildUrl(`/family-tree/${currentFamilyTreeId}?member=${encodeURIComponent(selectedMember.id)}`)}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: `${selectedMember.name} — Family Tree`,
          url,
        });
        return;
      }
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch (e) {
      // User cancelled share dialog or clipboard failed
      console.warn('Share failed:', e);
    }
  };

  const handleClose = () => {
    setSelectedMember(null);
  };

  const handleEdit = () => {
    setShowEditModal(true);
  };

  return (
    <>
      <div className="absolute inset-y-0 right-0 z-30 w-full sm:w-80 sm:static bg-white border-l border-gray-200 p-6 overflow-y-auto shadow-xl sm:shadow-none">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Member Details</h2>
          <div className="flex items-center space-x-2">
            {isAdminView && (
              <button
                onClick={handleEdit}
                className="p-2 rounded-md hover:bg-gray-100 text-gray-600 hover:text-gray-800"
                title="Edit"
              >
                <Edit className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={handleClose}
              className="p-2 rounded-md hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Profile Photo */}
        <div className="text-center mb-6">
          <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden bg-gray-200">
            {selectedMember.photoUrl ? (
              <img
                src={selectedMember.photoUrl}
                alt={selectedMember.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400 text-4xl font-bold">
                {selectedMember.name.charAt(0)}
              </div>
            )}
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{selectedMember.name}</h3>
          {selectedMember.nickname && (
            <p className="text-gray-600">"{selectedMember.nickname}"</p>
          )}
        </div>

        {/* Basic Information */}
        <div className="space-y-4 mb-8">
          <div className="flex items-center space-x-3">
            <Calendar className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-600">Birth Date</p>
              <p className="font-medium">
                {selectedMember.birthDate
                  ? new Date(selectedMember.birthDate).toLocaleDateString('en-US', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })
                  : 'Not recorded'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <Calendar className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-600">Age</p>
              <p className="font-medium">
                {typeof calculateAge(selectedMember.birthDate, selectedMember.deathDate) === 'number'
                  ? `${calculateAge(selectedMember.birthDate, selectedMember.deathDate)} years${!selectedMember.isAlive ? ' (dec.)' : ''}`
                  : 'Not recorded'}
              </p>
            </div>
          </div>

          {selectedMember.birthPlace && (
            <div className="flex items-center space-x-3">
              <MapPin className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">Birth Place</p>
                <p className="font-medium">{selectedMember.birthPlace}</p>
              </div>
            </div>
          )}

          {selectedMember.currentLocation && (
            <div className="flex items-center space-x-3">
              <MapPin className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">Current Location</p>
                <p className="font-medium">{selectedMember.currentLocation}</p>
              </div>
            </div>
          )}

          {selectedMember.profession && (
            <div className="flex items-center space-x-3">
              <Briefcase className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">Profession</p>
                <p className="font-medium">{selectedMember.profession}</p>
              </div>
            </div>
          )}

          {selectedMember.education && (
            <div className="flex items-center space-x-3">
              <GraduationCap className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">Education</p>
                <p className="font-medium">{selectedMember.education}</p>
              </div>
            </div>
          )}

          <div className="flex items-center space-x-3">
            <Heart className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-600">Marital Status</p>
              <p className="font-medium">
                {selectedMember.maritalStatus === 'married' && 'Married'}
                {selectedMember.maritalStatus === 'single' && 'Single'}
                {selectedMember.maritalStatus === 'divorced' && 'Divorced'}
                {selectedMember.maritalStatus === 'widowed' && 'Widowed'}
              </p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        {(selectedMember.phone || selectedMember.email || selectedMember.facebookUrl) && (
          <div className="mb-8">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Contact</h4>
            <div className="space-y-3">
              {selectedMember.phone && (
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <span className="text-sm">{selectedMember.phone}</span>
                </div>
              )}
              {selectedMember.email && (
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <span className="text-sm">{selectedMember.email}</span>
                </div>
              )}
              {selectedMember.facebookUrl && (
                <a
                  href={selectedMember.facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 hover:text-blue-600 transition-colors"
                >
                  <Link2 className="w-5 h-5 text-gray-400" />
                  <span className="text-sm">Facebook Profile</span>
                </a>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          {isAdminView && (
            <button 
              onClick={handleEdit}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Edit Profile
            </button>
          )}
          
          {selectedMember.phone && (
            <a
              href={`https://wa.me/${selectedMember.phone.replace(/[^0-9]/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
            >
              <MessageCircle className="w-4 h-4" />
              <span>WhatsApp</span>
            </a>
          )}
          
          {selectedMember.email && (
            <a
              href={`mailto:${selectedMember.email}`}
              className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors flex items-center justify-center space-x-2"
            >
              <Mail className="w-4 h-4" />
              <span>Email</span>
            </a>
          )}

          {selectedMember.facebookUrl && (
            <a
              href={selectedMember.facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full px-4 py-2 bg-blue-700 text-white rounded-md hover:bg-blue-800 transition-colors flex items-center justify-center space-x-2"
            >
              <Link2 className="w-4 h-4" />
              <span>Facebook</span>
            </a>
          )}
          
          <button
            onClick={handleShare}
            className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
          >
            <Share2 className="w-4 h-4" />
            <span>{shareCopied ? 'Link Copied!' : 'Share Profile'}</span>
          </button>
        </div>
      </div>

      <UnifiedMemberModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        editingMember={selectedMember}
      />
    </>
  );
};